import { getShellLogger } from '../../common/logger'
import { environment } from '../../environments'
import { authenticatedFetch } from '../auth'
import type { ProductTrial } from '../../services/context/models'
import { getStatusCode } from 'http-status-codes'
import type { retryOnOptions } from '../../common/retry'

/**
 * Makes a call to the G2M product billing API
 * NOTE: /rest/2/product API will always return 404 if the user is not on an active trial
 * @see {@link https://confluence.ops.expertcity.com/display/collaboration/Meeting+Service+REST+API+2.0#get-product-example-1}
 * @returns a parsed object of a user's G2M product billing data
 */
export const getProductTrialInfo = async (): Promise<ProductTrial> => {
  try {
    const response = await authenticatedFetch(`${environment().apiGlobal}/rest/2/product?clientName=true`, {
      headers: [['Content-Type', 'application/json']],
      credentials: undefined,
      retry: {
        retryOn,
      },
    })
    const { free, trial, product, endDate, active }: ProductTrial = await response.json()
    return { free, trial, product, endDate, active } as ProductTrial
  } catch (e) {
    logProductTrialAPIError(e)
  }
  return {} as ProductTrial
}

/**
 * Logs the severity of the HTTP error code received from Product Trial API
 * @param error
 */
export const logProductTrialAPIError = (error: Error | any) => {
  switch (error.status) {
    case getStatusCode('Not Found'):
      getShellLogger().info('User has no active trial')
      break

    default:
      getShellLogger().error('could not fetch Trial info', error)
      break
  }
}

const retryOn = async (options: retryOnOptions<Response>) => {
  const { error, value } = options
  const { status } = value ?? {}

  if (status === 404) {
    return false
  }
  if (error) {
    return true
  }

  return status ? status >= 400 : false
}
